import { useLayoutEffect, RefObject } from "react";
import { useLocation } from "react-router-dom";

/**
 * Per-tab, in-memory scroll memory keyed by route path — the visual counterpart
 * to the request cache (features/insights/utils/requestCache) that already keeps
 * each tab's DATA warm across navigation.
 *
 * The pages live under separate routes, so switching tabs unmounts the page's
 * DOM and rebuilds the scroll container at the top on return; the cached data
 * makes the content repaint instantly, but you still lose your place. This
 * records where each route was last left and puts it back, so a tab you were
 * halfway down reopens where you were.
 *
 * Module-scoped (survives in-session navigation, NOT a full refresh), mirroring
 * requestCache — a refresh starting at the top is fine and expected.
 */
const scrollPositions = new Map<string, number>();

/** Drop every recorded position. Used to isolate tests; not needed in app code. */
export function clearScrollPositions(): void {
  scrollPositions.clear();
}

/**
 * Remember and restore the scroll offset of `ref`'s element per route.
 *
 * Restore runs in a layout effect (before the browser paints, so there's no
 * flash at the top before jumping down). Because each tab's data is cached, the
 * content is at full height on the first paint back, so the saved offset is
 * reachable and sticks.
 *
 * The position is saved on every scroll (passive listener) and once more on
 * unmount, so the latest offset is captured even if the final scroll event was
 * coalesced away before the page unmounted.
 */
export function useScrollRestoration<T extends HTMLElement = HTMLElement>(
  ref: RefObject<T>,
): void {
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    el.scrollTop = scrollPositions.get(pathname) ?? 0;

    const handleScroll = () => {
      scrollPositions.set(pathname, el.scrollTop);
    };
    el.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      scrollPositions.set(pathname, el.scrollTop);
      el.removeEventListener("scroll", handleScroll);
    };
  }, [ref, pathname]);
}
